import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Coins, Zap, Loader, X, ExternalLink, Sparkles } from "lucide-react";

/**
 * Hover/click popover that shows a balance snapshot and lets the user top up
 * without leaving the current page. Wraps the navbar Credits pill.
 *
 * Behaviour:
 *  - On desktop: opens on hover, closes when the mouse leaves both the pill
 *    and the popover surface (with a 200ms grace so quick mouse darts don't
 *    re-open).
 *  - On focus / click: stays open until X or outside-click. Keyboard accessible.
 *  - Buy buttons hit `POST /billing/checkout` and redirect to Stripe.
 */
export default function CreditsPopover({ children, billing, anchorRef }) {
  const [open, setOpen] = useState(false);
  const [packs, setPacks] = useState([]);
  const [buying, setBuying] = useState(null);
  const [error, setError] = useState("");
  const closeTimer = useRef(null);
  const popoverRef = useRef(null);

  // Lazy-load the credit pack catalogue the first time the popover opens.
  useEffect(() => {
    if (open && packs.length === 0) {
      api
        .get("/billing/catalog")
        .then(({ data }) => {
          const catalog = data?.credit_packs || {};
          // Order: small, medium, large
          const order = ["small", "medium", "large"];
          setPacks(
            order
              .filter((k) => catalog[k])
              .map((k) => ({ id: k, ...catalog[k] }))
          );
        })
        .catch(() => setError("Couldn't load top-up options."));
    }
  }, [open, packs.length]);

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const buyPack = async (packId) => {
    setBuying(packId);
    setError("");
    try {
      const { data } = await api.post("/billing/checkout", {
        kind: "credit_pack",
        pack_id: packId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Checkout failed. Try again.");
      setBuying(null);
    }
  };

  const balance = billing?.credits_balance ?? 0;
  const isLow = balance < 25 && !billing?.is_pro;
  const discountActive = billing?.beta_lifetime_discount;
  const fmtPrice = (usd) => `$${usd.toFixed(2)}`;
  const applyDiscount = (usd) => (discountActive ? Math.round(usd * 0.8 * 100) / 100 : usd);

  return (
    <div
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="credits-popover-root"
    >
      {children}

      {open && !billing?.is_pro && (
        <div
          ref={popoverRef}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="rm-card-static absolute right-0 top-full mt-2 z-50 w-[340px] p-4"
          style={{
            background: "white",
            boxShadow: "6px 6px 0 0 #0F1B2D",
            animation: "rmPopIn 0.2s ease-out",
          }}
          data-testid="credits-popover"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 p-1 rounded hover:bg-slate-100"
            aria-label="Close"
            data-testid="credits-popover-close"
          >
            <X size={14} />
          </button>

          {/* Balance breakdown — 3 stat blocks: current, low-warning, top-up CTA */}
          <div className="rm-label mb-2 flex items-center gap-2">
            <Coins size={12} className="rm-text-orange" />
            Credits snapshot
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="p-3 rounded"
                 style={{
                   background: isLow ? "#FEF2F2" : "#F8FAFC",
                   border: isLow ? "2px solid #DC2626" : "2px solid #0F1B2D",
                 }}>
              <div className="font-mono-rm text-[9px] uppercase tracking-widest"
                   style={{ color: isLow ? "#991B1B" : "#475569" }}>
                Balance
              </div>
              <div className="font-display text-2xl leading-none mt-1"
                   style={{ color: isLow ? "#DC2626" : "#0F1B2D" }}
                   data-testid="credits-popover-balance">
                {balance}
              </div>
            </div>
            <div className="p-3 rounded"
                 style={{
                   background: isLow ? "#FFFBEB" : "#F0FDF4",
                   border: "2px solid #0F1B2D",
                 }}>
              <div className="font-mono-rm text-[9px] uppercase tracking-widest"
                   style={{ color: "#475569" }}>
                Status
              </div>
              <div className="font-display text-sm leading-tight mt-1"
                   style={{ color: isLow ? "#92400E" : "#065F46" }}>
                {isLow ? "Almost out" : "All good"}
              </div>
            </div>
          </div>

          {isLow && (
            <div className="text-xs mb-3 px-2 py-1.5 rounded font-mono-rm"
                 style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FCA5A5" }}>
              <strong>Heads up:</strong> you're below 25 credits. Top up to avoid blocked tailoring.
            </div>
          )}

          {/* Pack buttons */}
          <div className="rm-label mb-2 flex items-center gap-2">
            <Zap size={12} className="rm-text-orange" />
            One-click top-up
            {discountActive && (
              <span className="rm-tag text-[9px]"
                    style={{ background: "#FFC145", borderColor: "#0F1B2D", marginLeft: 6 }}>
                <Sparkles size={9} /> 20% beta discount
              </span>
            )}
          </div>

          {packs.length === 0 && !error && (
            <div className="flex justify-center py-3">
              <Loader size={16} className="animate-spin rm-text-orange" />
            </div>
          )}

          {error && (
            <div className="text-xs mb-2 px-2 py-1.5 rounded font-mono-rm"
                 style={{ background: "#FEF2F2", color: "#991B1B" }}>
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            {packs.map((pack) => {
              const price = applyDiscount(pack.amount_usd);
              return (
                <button
                  key={pack.id}
                  type="button"
                  onClick={() => buyPack(pack.id)}
                  disabled={buying !== null}
                  className="w-full flex items-center justify-between px-3 py-2 rounded text-left transition-all hover:translate-x-[1px] hover:-translate-y-[1px]"
                  style={{
                    background: pack.id === "large" ? "#FF6A00" : "white",
                    color: pack.id === "large" ? "white" : "#0F1B2D",
                    border: "2px solid #0F1B2D",
                    boxShadow: "3px 3px 0 0 #0F1B2D",
                  }}
                  data-testid={`credits-popover-buy-${pack.id}`}
                >
                  <div>
                    <div className="font-bold text-sm">
                      {pack.credits} credits
                      <span className="font-mono-rm text-[10px] uppercase ml-2 opacity-70">
                        · {pack.label}
                      </span>
                    </div>
                    <div className="font-mono-rm text-[10px] opacity-80">
                      {pack.description}
                    </div>
                  </div>
                  <div className="text-right">
                    {discountActive && (
                      <div className="font-mono-rm text-[9px] line-through opacity-60">
                        {fmtPrice(pack.amount_usd)}
                      </div>
                    )}
                    <div className="font-display text-lg leading-none">
                      {buying === pack.id
                        ? <Loader size={16} className="animate-spin" />
                        : fmtPrice(price)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <Link
            to="/billing"
            onClick={() => setOpen(false)}
            className="block mt-3 text-center font-mono-rm text-[10px] uppercase tracking-widest underline opacity-70 hover:opacity-100"
            data-testid="credits-popover-full-billing"
          >
            Full billing & history <ExternalLink size={9} className="inline ml-1" />
          </Link>
        </div>
      )}
    </div>
  );
}
